/**
 * Booking Helper Utilities
 * 
 * Utilities to handle the new multi-room, multi-bed, multi-course booking format
 * and provide backward compatibility with old booking format.
 * 
 * New Schema Structure:
 * - booking.bookedRooms[] (replaces booking.room)
 * - booking.bookedBeds[] (replaces booking.bed)
 * - booking.bookedCourses[] (replaces booking.course)
 */

/**
 * Get primary room from booking (first booked room)
 * @param {Object} booking - Booking object
 * @returns {Object|null} Room object with snapshot data
 */
export const getPrimaryRoom = (booking) => {
  if (!booking) return null;
  
  // New format: bookedRooms array
  if (booking.bookedRooms && booking.bookedRooms.length > 0) {
    const bookedRoom = booking.bookedRooms[0];
    return {
      id: bookedRoom.roomId,
      roomNumber: bookedRoom.roomNumber,
      roomType: {
        name: bookedRoom.roomTypeName,
        description: bookedRoom.roomTypeDesc,
        isShared: bookedRoom.isShared,
        basePrice: bookedRoom.basePrice
      },
      property: bookedRoom.room?.property || null,
      // Include the full room relation if available
      ...bookedRoom.room
    };
  }
  
  // Old format: direct room relation (backward compatibility)
  if (booking.room) {
    return booking.room;
  }
  
  return null;
};

/**
 * Get all rooms from booking
 * @param {Object} booking - Booking object
 * @returns {Array} Array of room objects
 */
export const getAllRooms = (booking) => {
  if (!booking) return [];
  
  // New format: bookedRooms array
  if (booking.bookedRooms && booking.bookedRooms.length > 0) {
    return booking.bookedRooms.map(bookedRoom => ({
      id: bookedRoom.roomId,
      bookedRoomId: bookedRoom.id,
      roomNumber: bookedRoom.roomNumber,
      roomType: bookedRoom.room?.roomType || {
        name: bookedRoom.roomTypeName,
        description: bookedRoom.roomTypeDesc,
        isShared: bookedRoom.isShared || bookedRoom.room?.roomType?.isShared,
        basePrice: bookedRoom.basePrice
      },
      checkIn: bookedRoom.checkIn,
      checkOut: bookedRoom.checkOut,
      nights: bookedRoom.nights,
      basePrice: bookedRoom.basePrice,
      finalAmount: bookedRoom.finalAmount,
      status: bookedRoom.status,
      notes: bookedRoom.notes,
      property: bookedRoom.room?.property || null,
      priceBreakdowns: bookedRoom.priceBreakdowns || [],
      // Include the full room relation if available
      ...bookedRoom.room
    }));
  }
  
  // Old format: single room (backward compatibility)
  if (booking.room) {
    return [booking.room];
  }
  
  return [];
};

/**
 * Get primary bed from booking (first booked bed)
 * @param {Object} booking - Booking object
 * @returns {Object|null} Bed object with snapshot data
 */
export const getPrimaryBed = (booking) => {
  if (!booking) return null;
  
  // New format: bookedBeds array
  if (booking.bookedBeds && booking.bookedBeds.length > 0) {
    const bookedBed = booking.bookedBeds[0];
    return {
      id: bookedBed.bedId,
      bedNumber: bookedBed.bedNumber,
      roomNumber: bookedBed.roomNumber,
      roomType: {
        name: bookedBed.roomTypeName
      },
      basePrice: bookedBed.basePrice,
      room: bookedBed.bed?.room || null,
      // Include the full bed relation if available
      ...bookedBed.bed
    };
  }
  
  // Old format: direct bed relation (backward compatibility)
  if (booking.bed) {
    return booking.bed;
  }
  
  return null;
};

/**
 * Get all beds from booking
 * @param {Object} booking - Booking object
 * @returns {Array} Array of bed objects
 */
export const getAllBeds = (booking) => {
  if (!booking) return [];
  
  // New format: bookedBeds array
  if (booking.bookedBeds && booking.bookedBeds.length > 0) {
    return booking.bookedBeds.map(bookedBed => ({
      id: bookedBed.bedId,
      bookedBedId: bookedBed.id,
      bedNumber: bookedBed.bedNumber,
      roomNumber: bookedBed.roomNumber,
      roomType: bookedBed.bed?.room?.roomType || {
        name: bookedBed.roomTypeName
      },
      checkIn: bookedBed.checkIn,
      checkOut: bookedBed.checkOut,
      nights: bookedBed.nights,
      basePrice: bookedBed.basePrice,
      finalAmount: bookedBed.finalAmount,
      status: bookedBed.status,
      notes: bookedBed.notes,
      room: bookedBed.bed?.room || null,
      property: bookedBed.bed?.room?.property || null,
      priceBreakdowns: bookedBed.priceBreakdowns || [],
      // Include the full bed relation if available
      ...bookedBed.bed
    }));
  }
  
  // Old format: single bed (backward compatibility)
  if (booking.bed) {
    return [booking.bed];
  }
  
  return [];
};

/**
 * Get primary course from booking (first booked course)
 * @param {Object} booking - Booking object
 * @returns {Object|null} Course object with snapshot data
 */
export const getPrimaryCourse = (booking) => {
  if (!booking) return null;
  
  // New format: bookedCourses array
  if (booking.bookedCourses && booking.bookedCourses.length > 0) {
    const bookedCourse = booking.bookedCourses[0];
    return {
      id: bookedCourse.courseId,
      name: bookedCourse.courseName,
      description: bookedCourse.courseDesc,
      fee: bookedCourse.fee,
      quantity: bookedCourse.quantity,
      finalAmount: bookedCourse.finalAmount,
      startDate: bookedCourse.course?.startDate || null,
      endDate: bookedCourse.course?.endDate || null,
      duration: bookedCourse.course?.duration || null,
      // Include the full course relation if available
      ...bookedCourse.course
    };
  }
  
  // Old format: direct course relation (backward compatibility)
  if (booking.course) {
    return booking.course;
  }
  
  return null;
};

/**
 * Get all courses from booking
 * @param {Object} booking - Booking object
 * @returns {Array} Array of course objects
 */
export const getAllCourses = (booking) => {
  if (!booking) return [];
  
  // New format: bookedCourses array
  if (booking.bookedCourses && booking.bookedCourses.length > 0) {
    return booking.bookedCourses.map(bookedCourse => ({
      id: bookedCourse.courseId,
      bookedCourseId: bookedCourse.id,
      name: bookedCourse.courseName,
      description: bookedCourse.courseDesc || bookedCourse.course?.description,
      fee: bookedCourse.fee || bookedCourse.course?.fee,
      quantity: bookedCourse.quantity,
      finalAmount: bookedCourse.finalAmount,
      status: bookedCourse.status,
      notes: bookedCourse.notes,
      startDate: bookedCourse.course?.startDate || null,
      endDate: bookedCourse.course?.endDate || null,
      duration: bookedCourse.course?.duration || null,
      priceBreakdowns: bookedCourse.priceBreakdowns || [],
      // Include the full course relation if available
      ...bookedCourse.course
    }));
  }
  
  // Old format: single course (backward compatibility)
  if (booking.course) {
    return [booking.course];
  }
  
  return [];
};

/**
 * Get course count from booking (for backward compatibility)
 * @param {Object} booking - Booking object
 * @returns {number} Total course quantity
 */
export const getCourseCount = (booking) => {
  if (!booking) return 0;
  
  // New format: sum quantities from bookedCourses
  if (booking.bookedCourses && booking.bookedCourses.length > 0) {
    return booking.bookedCourses.reduce((sum, bc) => sum + (bc.quantity || 0), 0);
  }
  
  // Old format: direct courseCount field
  if (booking.courseCount !== undefined) {
    return booking.courseCount;
  }
  
  return 0;
};

/**
 * Get guest count from booking
 * @param {Object} booking - Booking object
 * @returns {number} Guest count
 */
export const getGuestCount = (booking) => {
  if (!booking) return 0;
  
  // Guest count is still at booking level
  return booking.guestCount || 0;
};

/**
 * Get accommodation display string (room/bed info)
 * @param {Object} booking - Booking object
 * @returns {string} Formatted accommodation string
 */
export const getAccommodationDisplay = (booking) => {
  if (!booking) return 'N/A';
  
  const rooms = getAllRooms(booking);
  const beds = getAllBeds(booking);
  
  if (rooms.length > 0) {
    if (rooms.length === 1) {
      return `Room ${rooms[0].roomNumber}`;
    }
    return `${rooms.length} Rooms: ${rooms.map(r => r.roomNumber).join(', ')}`;
  }
  
  if (beds.length > 0) {
    if (beds.length === 1) {
      return `Bed ${beds[0].bedNumber} (Room ${beds[0].roomNumber})`;
    }
    return `${beds.length} Beds`;
  }
  
  return 'N/A';
};

/**
 * Get course display string
 * @param {Object} booking - Booking object
 * @returns {string} Formatted course string
 */
export const getCourseDisplay = (booking) => {
  if (!booking) return 'N/A';
  
  const courses = getAllCourses(booking);
  
  if (courses.length === 0) {
    return 'Staycation';
  }
  
  if (courses.length === 1) {
    const course = courses[0];
    return `${course.name}${course.quantity > 1 ? ` (x${course.quantity})` : ''}`;
  }
  
  return `${courses.length} Courses`;
};

/**
 * Check if booking is staycation (no courses)
 * @param {Object} booking - Booking object
 * @returns {boolean} True if staycation
 */
export const isStaycation = (booking) => {
  if (!booking) return false;
  
  const courses = getAllCourses(booking);
  return courses.length === 0;
};

/**
 * Get property name from booking
 * @param {Object} booking - Booking object
 * @returns {string} Property name or 'N/A'
 */
export const getPropertyName = (booking) => {
  if (!booking) return 'N/A';
  
  // Try to get from booked rooms
  const rooms = getAllRooms(booking);
  if (rooms.length > 0 && rooms[0].property) {
    return rooms[0].property.name;
  }
  
  // Try to get from booked beds
  const beds = getAllBeds(booking);
  if (beds.length > 0 && beds[0].property) {
    return beds[0].property.name;
  }
  
  return 'N/A';
};

/**
 * Calculate total accommodation cost (rooms + beds)
 * @param {Object} booking - Booking object
 * @returns {number} Total accommodation cost
 */
export const getTotalAccommodationCost = (booking) => {
  if (!booking) return 0;
  
  // Use pricing.accommodationTotal from new API if available
  if (booking.pricing?.accommodationTotal !== undefined) {
    return booking.pricing.accommodationTotal;
  }
  
  let total = 0;
  
  // Sum up room costs
  const rooms = getAllRooms(booking);
  rooms.forEach(room => {
    total += room.finalAmount || 0;
  });
  
  // Sum up bed costs
  const beds = getAllBeds(booking);
  beds.forEach(bed => {
    total += bed.finalAmount || 0;
  });
  
  return total;
};

/**
 * Calculate total course cost
 * @param {Object} booking - Booking object
 * @returns {number} Total course cost
 */
export const getTotalCourseCost = (booking) => {
  if (!booking) return 0;
  
  // Use pricing.coursesTotal from new API if available
  if (booking.pricing?.coursesTotal !== undefined) {
    return booking.pricing.coursesTotal;
  }
  
  const courses = getAllCourses(booking);
  return courses.reduce((sum, course) => sum + (course.finalAmount || 0), 0);
};

/**
 * Get price breakdown for display
 * @param {Object} booking - Booking object
 * @returns {Object} Price breakdown object
 */
export const getPriceBreakdown = (booking) => {
  if (!booking) {
    return {
      accommodation: 0,
      courses: 0,
      addons: 0,
      total: 0
    };
  }
  
  // Use pricing object from new API if available
  if (booking.pricing) {
    return {
      accommodation: booking.pricing.accommodationTotal || 0,
      courses: booking.pricing.coursesTotal || 0,
      addons: booking.pricing.addOnsTotal || 0,
      total: booking.pricing.grandTotal || booking.pricing.finalAmount || 0,
      rooms: booking.pricing.roomsTotal || 0,
      beds: booking.pricing.bedsTotal || 0
    };
  }
  
  // Fallback to calculated values
  const accommodationCost = getTotalAccommodationCost(booking);
  const courseCost = getTotalCourseCost(booking);
  const addonsCost = booking.addOns?.reduce((sum, addon) => sum + (addon.finalAmount || addon.price || 0), 0) || 0;
  
  return {
    accommodation: accommodationCost,
    courses: courseCost,
    addons: addonsCost,
    total: booking.finalAmount || booking.totalPrice || 0
  };
};

/**
 * Format booking for legacy components expecting old format
 * @param {Object} booking - Booking object (new format)
 * @returns {Object} Booking in old format
 */
export const toLegacyFormat = (booking) => {
  if (!booking) return null;
  
  const primaryRoom = getPrimaryRoom(booking);
  const primaryBed = getPrimaryBed(booking);
  const primaryCourse = getPrimaryCourse(booking);
  
  return {
    ...booking,
    // Add old format fields
    room: primaryRoom,
    roomId: primaryRoom?.id || null,
    bed: primaryBed,
    bedId: primaryBed?.id || null,
    course: primaryCourse,
    courseId: primaryCourse?.id || null,
    courseCount: getCourseCount(booking)
  };
};

/**
 * Format booking request for new API from form data
 * @param {Object} formData - Form data object
 * @returns {Object} Booking request in new format
 */
export const formatBookingRequest = (formData) => {
  return {
    userId: formData.userId,
    checkIn: formData.checkIn,
    checkOut: formData.checkOut,
    rooms: formData.rooms || (formData.roomId ? [{
      roomId: formData.roomId,
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      notes: formData.roomNotes || null
    }] : []),
    beds: formData.beds || (formData.bedId ? [{
      bedId: formData.bedId,
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      notes: formData.bedNotes || null
    }] : []),
    courses: formData.courses || (formData.courseId ? [{
      courseId: formData.courseId,
      quantity: formData.courseCount || 1,
      notes: formData.courseNotes || null
    }] : []),
    addOns: formData.addOns || [],
    notes: formData.notes || null,
    bookingPlatform: formData.bookingPlatform || 'BOOKING_ENGINE'
  };
};
